import { CirclePlus, CopyPlus, EllipsisVertical, FileSignature, OctagonX } from 'lucide-react'
import { useState } from 'react'

import type { TreatmentPrescriptionContext, TreatmentWithRelations } from '@snootsvet/shared'
import { getPrimaryOrder } from '@snootsvet/shared'

import { DrawerOverlay } from '@/frontend/components/drawer-overlay'
import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import { CancelTreatmentDialog } from '@/frontend/features/consultations/actions/cancel-treatment-dialog'
import { RefillDialog } from '@/frontend/features/consultations/actions/refill-treatment-dialog'
import { QuickPrescribePanel } from '@/frontend/features/consultations/quick-prescribe-panel'
import { toReprescribeItem } from '@/frontend/features/consultations/treatments.utils'
import { WrittenPrescriptionDialog } from '@/frontend/features/documents/written-prescription.form'

type PrescriptionActionProps = {
  rx: TreatmentWithRelations & TreatmentPrescriptionContext
  patientId: string
  nested?: boolean
}
export function useRefillPrescription({ rx, patientId, nested }: PrescriptionActionProps) {
  const [open, setOpen] = useState(false)
  return {
    menuItem:
      rx.isActivePrescription && !rx.isExternal ? (
        <Tooltip disabled={rx.isEligibleForRefill}>
          <TooltipTrigger
            render={
              <DropdownMenuItem
                disabled={!rx.isEligibleForRefill}
                onClick={() => setOpen(true)}
                className="data-disabled:pointer-events-auto data-disabled:cursor-not-allowed"
              />
            }
          >
            <CirclePlus className="size-3.5" />
            Refill
          </TooltipTrigger>
          <TooltipContent>
            {rx.refillsUsed >= (rx.repeatsAllowed ?? 0) ? (
              'All authorised refills have been used.'
            ) : rx.hasActiveOrder ? (
              'An order for this prescription is already being processed.'
            ) : rx.refillCooldownEndsAt && new Date(rx.refillCooldownEndsAt) > new Date() ? (
              <>
                Next refill available from{' '}
                <FormattedDate
                  value={rx.refillCooldownEndsAt}
                  clinicId={getPrimaryOrder(rx.orders)?.clinicId ?? null}
                />
                .
              </>
            ) : (
              'This prescription is not currently eligible for refill.'
            )}
          </TooltipContent>
        </Tooltip>
      ) : null,
    dialog: (
      <RefillDialog
        rx={rx}
        patientId={patientId}
        open={open}
        onOpenChange={setOpen}
        nested={nested}
      />
    ),
  }
}

export function useReprescribePrescription({ rx, patientId, nested }: PrescriptionActionProps) {
  const [open, setOpen] = useState(false)
  return {
    trigger: () => setOpen(true),
    dialog: (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent nested={nested} className="sm:max-w-2xl">
          <DrawerOverlay>
            <DialogHeader>
              <DialogTitle>Re-prescribe - {rx.productInfo.name}</DialogTitle>
            </DialogHeader>
            <QuickPrescribePanel
              patientId={patientId}
              defaultValues={toReprescribeItem(rx)}
              onSuccess={() => setOpen(false)}
              onCancel={() => setOpen(false)}
            />
          </DrawerOverlay>
        </DialogContent>
      </Dialog>
    ),
  }
}

export function useGeneratePrescriptionScript({ rx, nested }: PrescriptionActionProps) {
  const [open, setOpen] = useState(false)
  return {
    available: rx.isExternal,
    trigger: () => setOpen(true),
    dialog:
      rx.isExternal && open ? (
        <WrittenPrescriptionDialog
          prescription={rx}
          nested={nested}
          open={open}
          onOpenChange={setOpen}
        />
      ) : null,
  }
}

function useCancelPrescription({ rx, patientId, nested }: PrescriptionActionProps) {
  const [open, setOpen] = useState(false)
  return {
    available: !rx.cancelledAt,
    trigger: () => setOpen(true),
    dialog: (
      <CancelTreatmentDialog
        rx={rx}
        patientId={patientId}
        open={open}
        onOpenChange={setOpen}
        nested={nested}
      />
    ),
  }
}

export function PrescriptionActionsMenu({
  rx,
  patientId,
  nested = false,
}: PrescriptionActionProps) {
  const refill = useRefillPrescription({ rx, patientId, nested })
  const represcribe = useReprescribePrescription({ rx, patientId, nested })
  const script = useGeneratePrescriptionScript({ rx, patientId, nested })
  const cancel = useCancelPrescription({ rx, patientId, nested })

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Actions for ${rx.productInfo.name}`}
            />
          }
        >
          <EllipsisVertical className="size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          {refill.menuItem}
          <DropdownMenuItem onClick={represcribe.trigger}>
            <CopyPlus className="size-3.5" />
            Re-prescribe
          </DropdownMenuItem>
          {script.available && (
            <DropdownMenuItem onClick={script.trigger}>
              <FileSignature className="size-3.5" />
              Generate Script
            </DropdownMenuItem>
          )}
          {cancel.available && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={cancel.trigger}>
                <OctagonX className="size-3.5" />
                Cancel Treatment
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {refill.dialog}
      {represcribe.dialog}
      {script.dialog}
      {cancel.dialog}
    </>
  )
}
